/**
 * Payments Table - Lists payments with method, date, linked invoice
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Plus, CreditCard, Ban } from 'lucide-react';
import { formatCurrency, formatDate, PAYMENT_STATUS_CONFIG, PAYMENT_METHODS } from './utils';

// Status chip
const StatusChip = ({ status }) => {
  const statusInfo = PAYMENT_STATUS_CONFIG[status] || { label: status, color: 'bg-slate-100 text-slate-600' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
      {statusInfo.label}
    </span>
  );
};

// Get payment method label
const getMethodLabel = (method) => {
  const found = PAYMENT_METHODS.find(m => m.value === method);
  return found?.label || method?.replace(/_/g, ' ') || '—';
};

export const PaymentsTable = ({ 
  payments = [], 
  canEdit, 
  onRecordPayment,
  onVoidPayment,
  onViewPayment
}) => {
  return (
    <Card data-testid="payments-table">
      <CardHeader className="flex flex-row items-center justify-between py-4">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-green-500" />
          Payments Received
        </CardTitle>
        {canEdit && (
          <Button 
            size="sm" 
            onClick={onRecordPayment}
            data-testid="record-payment-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Record Payment
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {payments.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payment #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Applied</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Status</TableHead>
                  {canEdit && <TableHead className="w-[80px]"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map(pmt => (
                  <TableRow 
                    key={pmt.id}
                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    onClick={() => onViewPayment?.(pmt)}
                    data-testid={`payment-row-${pmt.id}`}
                  >
                    <TableCell className="font-medium">
                      {pmt.transaction_number || `PMT-${pmt.id?.slice(0, 8)}`}
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {formatDate(pmt.transaction_date || pmt.payment_date)}
                    </TableCell>
                    <TableCell className="capitalize">
                      {getMethodLabel(pmt.payment_method)}
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {pmt.reference_number || '—'}
                    </TableCell>
                    <TableCell className="text-right font-medium text-green-600">
                      {formatCurrency(pmt.applied_amount || pmt.amount)}
                    </TableCell>
                    <TableCell className="text-right">
                      {pmt.unapplied_amount > 0 ? (
                        <span className="text-amber-600" title="Includes unapplied credit">
                          {formatCurrency(pmt.total_amount)}
                        </span>
                      ) : (
                        formatCurrency(pmt.total_amount || pmt.amount)
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {pmt.line_items?.[0]?.invoice_number || '—'}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={pmt.status} />
                    </TableCell>
                    {canEdit && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {pmt.status === 'posted' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => onVoidPayment?.(pmt)}
                            title="Void payment"
                            data-testid={`void-payment-${pmt.id}`}
                          >
                            <Ban className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8">
            <CreditCard className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            <p className="text-slate-500">No payments recorded</p>
            {canEdit && (
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-3"
                onClick={onRecordPayment}
              >
                <Plus className="h-4 w-4 mr-1" />
                Record First Payment
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentsTable;
